import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayoutApple, ButtonApple, BadgeApple } from '@/components/apple-ui';
import {
  ArrowLeft, ExternalLink, Calendar, Globe, User, Hash, FileText, BookOpen,
  Database, List, ChevronDown, ChevronUp, Loader2, Edit, Briefcase,
  File, Download, Trash2, Plus, Search, X, CheckCircle2, AlertCircle,
  Clock, Tag, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import JobsModal from './JobsModal';
import '@/styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' },
  { code: 'gu', name: 'Gujarati' }, { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' }, { code: 'pa', name: 'Punjabi' },
  { code: 'mr', name: 'Marathi' }, { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' }, { code: 'bn', name: 'Bengali' },
  { code: 'or', name: 'Odia' }, { code: 'as', name: 'Assamese' },
  { code: 'ur', name: 'Urdu' }, { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' }, { code: 'es', name: 'Spanish' },
];

const getLangName = (code) => {
  const l = LANGUAGES.find(l => l.code === code || l.name.toLowerCase() === code?.toLowerCase());
  return l ? l.name : (code || '—');
};

const pid = (p) => p?.project_id || p?.id;

function deadlineBadge(deadline) {
  if (!deadline) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(deadline);
  const days = Math.round((due - today) / 86400000);
  if (days < 0)  return { label: `Overdue by ${Math.abs(days)} days`, cls: 'bg-red-50 text-red-700 border-red-100', icon: AlertCircle };
  if (days <= 3) return { label: `Due in ${days} day${days !== 1 ? 's' : ''}`, cls: 'bg-amber-50 text-amber-700 border-amber-100', icon: Clock };
  return {
    label: due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
    cls: 'bg-green-50 text-green-700 border-green-100',
    icon: CheckCircle2,
  };
}

// ─── Collapsible Section ─────────────────────────────────────────────────────
const Section = ({ id, title, icon: Icon, badge, children, defaultOpen = true, isDarkMode }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`} data-testid={`section-${id}`}>
      <button
        className={`w-full flex items-center justify-between px-6 py-4 text-left transition-colors ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-blue-600 flex-shrink-0" />
          <span className={`font-semibold text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{title}</span>
          {badge != null && (
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{badge}</span>
          )}
        </div>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {open && (
        <div className={`px-6 pb-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="pt-5">{children}</div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ProjectDetailPage = ({ user, planInfo, onLogout }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [project, setProject]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [tmStats, setTmStats]   = useState(null);
  const [glossary, setGlossary] = useState([]);
  const [glossarySearch, setGlossarySearch] = useState('');
  const [showJobsModal, setShowJobsModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setError(res.status === 404 ? 'Project not found.' : 'Failed to load project.'); return; }
      const data = await res.json();
      setProject(data);
    } catch { setError('Failed to load project.'); }
    finally { setLoading(false); }
  }, [projectId]);

  const fetchTmStats = useCallback(async () => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/tm/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTmStats(await res.json());
    } catch { /* non-critical */ }
  }, [projectId]);

  const fetchGlossary = useCallback(async () => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/glossary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGlossary(data.glossary?.terms || []);
      }
    } catch { /* non-critical */ }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchTmStats();
    fetchGlossary();
  }, [fetchProject, fetchTmStats, fetchGlossary]);

  if (loading) {
    return (
      <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
        <div className="apple-content flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-blue-600" size={40} />
        </div>
      </MainLayoutApple>
    );
  }

  if (error || !project) {
    return (
      <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
        <div className="apple-content text-center py-32">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error || 'Project not found'}</h2>
          <button onClick={() => navigate('/projects')} className="text-blue-600 hover:underline text-sm">← Back to Projects</button>
        </div>
      </MainLayoutApple>
    );
  }

  const progress = project.progress || 0;
  const isCompleted = project.status === 'completed' || progress === 100;
  const dl = deadlineBadge(project.deadline);
  const DlIcon = dl?.icon;
  const filteredGlossary = glossary.filter(t =>
    !glossarySearch || t.source?.toLowerCase().includes(glossarySearch.toLowerCase()) || t.target?.toLowerCase().includes(glossarySearch.toLowerCase())
  );

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content max-w-5xl" data-testid="project-detail-page">

        {/* Back + Actions bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/projects')}
            className={`flex items-center gap-2 text-sm font-medium transition-colors ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-900'}`}
          >
            <ArrowLeft size={16} /> All Projects
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowJobsModal(true)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              <Briefcase size={15} /> Manage Jobs
            </button>
            <ButtonApple
              icon={ExternalLink}
              onClick={() => navigate(`/cat?project=${pid(project)}`)}
              data-testid="open-cat-btn"
            >
              Open in CAT Editor
            </ButtonApple>
          </div>
        </div>

        {/* PROJECT OVERVIEW */}
        <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <h1 className={`text-2xl font-bold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{project.name}</h1>
                <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                  project.archived ? 'bg-gray-100 text-gray-500 border-gray-200'
                  : isCompleted ? 'bg-green-50 text-green-700 border-green-100'
                  : 'bg-blue-50 text-blue-700 border-blue-100'
                }`}>
                  {project.archived ? 'Archived' : isCompleted ? 'Completed' : 'In Progress'}
                </span>
                {project.workflow_status && (
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                    project.workflow_status === 'approved' ? 'bg-green-50 text-green-700 border-green-100'
                    : project.workflow_status === 'rejected' ? 'bg-red-50 text-red-700 border-red-100'
                    : project.workflow_status === 'in_review' ? 'bg-purple-50 text-purple-700 border-purple-100'
                    : project.workflow_status === 'ready_for_review' ? 'bg-yellow-50 text-yellow-700 border-yellow-100'
                    : 'bg-blue-50 text-blue-700 border-blue-100'
                  }`}>
                    {project.workflow_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                )}
              </div>
              <div className={`text-xs font-mono flex items-center gap-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Hash size={11} />
                {pid(project)}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Translation Progress</span>
              <span className={`text-sm font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{progress}%</span>
            </div>
            <div className={`h-2.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div
                className={`h-full rounded-full transition-all ${isCompleted ? 'bg-green-500' : progress >= 60 ? 'bg-blue-500' : progress >= 30 ? 'bg-amber-500' : 'bg-gray-300'}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            {(project.segment_count > 0) && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{project.confirmed_count || 0} of {project.segment_count} segments confirmed</p>
            )}
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Languages */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Globe size={11} /> Languages
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                  {getLangName(project.source_lang || project.source_language)}
                </span>
                <span className="text-gray-300 text-xs">→</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700'}`}>
                  {getLangName(project.target_lang || project.target_language)}
                </span>
              </div>
            </div>

            {/* Owner */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <User size={11} /> Owner
              </p>
              <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {project.owner_email || project.email || '—'}
              </p>
            </div>

            {/* Created */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Calendar size={11} /> Created
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {project.created_at
                  ? new Date(project.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
                  : '—'}
              </p>
            </div>

            {/* Deadline */}
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Clock size={11} /> Deadline
              </p>
              {dl ? (
                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg border font-medium ${dl.cls}`}>
                  <DlIcon size={11} /> {dl.label}
                </span>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No deadline</p>
              )}
            </div>
          </div>

          {/* Additional metadata row */}
          {(project.domain || project.instruction_name || (project.tags && project.tags.length > 0) || project.client) && (
            <div className={`flex flex-wrap gap-4 mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              {project.domain && project.domain !== 'general' && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Tag size={12} className="text-gray-400" />
                  <span className="font-medium">Domain:</span> {project.domain}
                </div>
              )}
              {project.instruction_name && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <BookOpen size={12} className="text-gray-400" />
                  <span className="font-medium">Instructions:</span> {project.instruction_name}
                </div>
              )}
              {project.client && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Building2 size={12} className="text-gray-400" />
                  <span className="font-medium">Client:</span> {project.client}
                </div>
              )}
              {project.tags?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {project.tags.map(tag => (
                    <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-4">

          {/* JOBS */}
          <Section id="jobs" title="Jobs" icon={Briefcase} badge={project.instruction_files?.length || 0} isDarkMode={isDarkMode} defaultOpen>
            {project.instruction_files && project.instruction_files.length > 0 ? (
              <div className="space-y-2 mb-4">
                {project.instruction_files.map((file, i) => (
                  <div key={i} className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={16} className="text-blue-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{file.filename}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {file.type?.toUpperCase()} · {file.size ? `${(file.size / 1024).toFixed(0)} KB` : ''} · {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-8 rounded-xl ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <Briefcase size={32} className="text-gray-300 mx-auto mb-3" />
                <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No jobs yet</p>
                <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Upload a document in the CAT Editor to start translating under this project</p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              <ButtonApple icon={ExternalLink} onClick={() => navigate(`/cat?project=${pid(project)}`)} data-testid="jobs-open-cat-btn">
                Open CAT Editor
              </ButtonApple>
              <button onClick={() => setShowJobsModal(true)} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${isDarkMode ? 'border-gray-700 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <Briefcase size={14} /> Manage Jobs
              </button>
            </div>
          </Section>

          {/* TRANSLATION MEMORY */}
          <Section id="tm" title="Translation Memory" icon={Database} badge={tmStats?.total_entries || 0} isDarkMode={isDarkMode} defaultOpen={false}>
            {tmStats ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Total Entries', value: tmStats.total_entries || 0 },
                    { label: 'Languages', value: tmStats.language_pairs?.length || 0 },
                    { label: 'Avg. Usage', value: tmStats.avg_usage_count != null ? tmStats.avg_usage_count.toFixed(1) : '—' },
                  ].map(stat => (
                    <div key={stat.label} className={`rounded-xl p-3 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stat.value}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</p>
                    </div>
                  ))}
                </div>
                {tmStats.language_pairs?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tmStats.language_pairs.map((lp, i) => (
                      <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-blue-50 text-blue-700'}`}>{lp}</span>
                    ))}
                  </div>
                )}
                <ButtonApple variant="ghost" onClick={() => navigate(`/tm`)} className="text-sm">
                  Open TM Manager →
                </ButtonApple>
              </div>
            ) : (
              <div className={`text-center py-6 rounded-xl ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <Database size={28} className="text-gray-300 mx-auto mb-2" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No TM entries yet</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Confirmed translations are automatically added to TM</p>
              </div>
            )}
          </Section>

          {/* GLOSSARY */}
          <Section id="glossary" title="Glossary" icon={List} badge={glossary.length} isDarkMode={isDarkMode} defaultOpen={false}>
            {glossary.length > 0 ? (
              <>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                  <input
                    type="text"
                    placeholder="Search terms..."
                    value={glossarySearch}
                    onChange={e => setGlossarySearch(e.target.value)}
                    className={`w-full pl-8 pr-3 py-2 text-sm rounded-xl border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-800'} focus:outline-none`}
                  />
                  {glossarySearch && (
                    <button onClick={() => setGlossarySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"><X size={12} /></button>
                  )}
                </div>
                <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="min-w-full text-sm">
                    <thead className={isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}>
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Target</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                      {filteredGlossary.slice(0, 50).map((term, i) => (
                        <tr key={i} className={isDarkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                          <td className={`px-4 py-2.5 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{term.source}</td>
                          <td className={`px-4 py-2.5 ${isDarkMode ? 'text-green-400' : 'text-green-700'} font-medium`}>{term.target}</td>
                          <td className={`px-4 py-2.5 text-xs hidden md:table-cell ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{term.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredGlossary.length > 50 && (
                    <div className={`px-4 py-2.5 text-xs text-center ${isDarkMode ? 'text-gray-500 bg-gray-700/30' : 'text-gray-400 bg-gray-50'}`}>
                      Showing first 50 of {filteredGlossary.length} terms
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <button onClick={() => navigate('/glossary')} className="text-xs text-blue-600 hover:underline">
                    Manage all glossary terms →
                  </button>
                </div>
              </>
            ) : (
              <div className={`text-center py-6 rounded-xl ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <List size={28} className="text-gray-300 mx-auto mb-2" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No glossary terms yet</p>
                <button onClick={() => navigate(`/cat?project=${pid(project)}`)} className="text-xs text-blue-600 hover:underline mt-1 block">
                  Add terms in the CAT Editor →
                </button>
              </div>
            )}
          </Section>

          {/* REFERENCES (Instruction Files) */}
          <Section id="references" title="References" icon={FileText} badge={project.instruction_files?.length || 0} isDarkMode={isDarkMode} defaultOpen={false}>
            {project.instruction_files && project.instruction_files.length > 0 ? (
              <div className="space-y-2">
                {project.instruction_files.map((file, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <File size={16} className="text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{file.filename}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {file.type?.toUpperCase()} · {file.size ? `${(file.size / 1024).toFixed(0)} KB` : ''} · Uploaded {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : 'recently'}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-500'}`}>Reference</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`text-center py-6 rounded-xl ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <FileText size={28} className="text-gray-300 mx-auto mb-2" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No reference files</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>Upload style guides or reference documents when creating a project</p>
              </div>
            )}
          </Section>

          {/* PROJECT INSTRUCTIONS */}
          <Section id="instructions" title="Project Instructions" icon={BookOpen} isDarkMode={isDarkMode} defaultOpen={false}>
            {(project.prebuilt_instructions?.length > 0 || project.custom_instructions) ? (
              <div className="space-y-4">
                {project.prebuilt_instructions?.length > 0 && (
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pre-built</p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.prebuilt_instructions.map((instr, i) => (
                        <span key={i} className={`text-xs px-2.5 py-1 rounded-full border font-medium ${isDarkMode ? 'bg-blue-900/30 border-blue-800/50 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
                          {instr}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {project.custom_instructions && (
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Custom Instructions</p>
                    <div className={`p-4 rounded-xl text-sm whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                      {project.custom_instructions}
                    </div>
                  </div>
                )}
                {project.instruction_name && (
                  <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Instruction set: <span className="font-medium">{project.instruction_name}</span>
                  </p>
                )}
              </div>
            ) : (
              <div className={`text-center py-6 rounded-xl ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <BookOpen size={28} className="text-gray-300 mx-auto mb-2" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No instructions configured</p>
              </div>
            )}
          </Section>
        </div>

        {/* Workflow comment banner */}
        {project.workflow_comment && (
          <div className={`mt-4 p-4 rounded-2xl border ${isDarkMode ? 'bg-red-900/20 border-red-800/50 text-red-300' : 'bg-red-50 border-red-100 text-red-700'}`}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1">Reviewer Feedback</p>
            <p className="text-sm">{project.workflow_comment}</p>
            {project.workflow_updated_by && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-red-500' : 'text-red-400'}`}>— {project.workflow_updated_by}</p>
            )}
          </div>
        )}
      </div>

      {/* Jobs Modal */}
      {showJobsModal && <JobsModal project={project} user={user} onClose={() => setShowJobsModal(false)} />}
    </MainLayoutApple>
  );
};

export default ProjectDetailPage;
